/**
 * TestRunSidePanelV2 — V2 port of V1's TestRunSidePanel (G177).
 *
 * V1's "Test Run Center" lived on AgentsPage.jsx (lines 4809-5300). V2 had
 * no equivalent — the ActTab's "Live execution feed" was explicitly stubbed
 * out as future work. This component restores parity: a chat-style side
 * panel that exercises the same `POST /api/v1/agents/{id}/test-run/chat`
 * endpoint, surfaces policy enforcement badges, tracks tool-call results,
 * and marks the agent "test passed" on the first successful round-trip.
 *
 * Differences from V1:
 *   - V2 design tokens (bg-v2-*, text-v2-*) instead of hardcoded #hex.
 *   - Reuses the v2 Modal / Pill / Button primitives.
 *   - Keyboard-friendly send (Enter, Shift+Enter newline).
 *   - Single self-contained file — no special globals.
 *
 * Props:
 *   - isOpen        : boolean
 *   - agent         : { id, name, ... }
 *   - onClose       : () => void
 *   - onTestPassed  : () => void    (fired on first successful round trip)
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  X, Maximize2, Minimize2, Shield, ShieldOff, ShieldCheck, AlertTriangle,
  CheckCircle, XCircle, Bot, MessageSquare, FlaskConical, Send, Zap,
} from 'lucide-react';
import { Pill } from './index';
import { useTestRun } from '../../hooks/useTestRun';

const POLICY_BADGE = {
  BLOCKED:  { tone: 'danger',  Icon: ShieldOff,    label: 'BLOCKED' },
  ENFORCED: { tone: 'warning', Icon: Shield,       label: 'POLICY ENFORCED' },
  ALLOWED:  { tone: 'success', Icon: ShieldCheck,  label: 'ALLOWED' },
  FLAGGED:  { tone: 'warning', Icon: AlertTriangle, label: 'FLAGGED' },
};

const SUGGESTED_PROMPTS = [
  'What can you do?',
  'List your skills',
  'Run a smoke test',
];

export function TestRunSidePanelV2({ isOpen, agent, onClose, onTestPassed }) {
  /* The conversation and what the platform made of it belong to
     hooks/useTestRun.js — Protect's Run Center exercises the same agents with
     the same policy live, and a second copy of "was this blocked" is how two
     screens come to disagree about it. The v2 rendering below stays here. */
  const { messages, sending, passed: testPassed, send, reset } =
    useTestRun(agent, { onFirstPass: onTestPassed });
  const [input, setInput] = useState('');
  const [expanded, setExpanded] = useState(true);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  /* Reset on agent change or open. */
  useEffect(() => {
    if (!isOpen) return;
    reset(); setInput('');
    setTimeout(() => inputRef.current?.focus(), 200);
  }, [isOpen, agent?.id, reset]);

  /* Auto-scroll on new messages. */
  useEffect(() => {
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
  }, [messages]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || sending) return;
    setInput('');
    await send(trimmed);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen || !agent) return null;
  const panelWidth = expanded ? 'min(48vw, 720px)' : '420px';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Test Run side panel"
      className="fixed top-0 right-0 bottom-0 z-50 flex flex-col bg-v2-page border-l border-v2-border-subtle shadow-v2-modal"
      style={{ width: panelWidth, transition: 'width 0.2s ease' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-v2-border-subtle bg-gradient-to-r from-v2-cyan/10 to-v2-action/5">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-v2-md bg-v2-cyan-soft text-v2-cyan flex items-center justify-center flex-shrink-0">
            <FlaskConical size={15} />
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-bold text-v2-ink tracking-tight">Test Run Center</div>
            <div className="text-[10.5px] text-v2-ink-muted truncate">{agent.name} — embedded test with policy enforcement</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {testPassed && <Pill tone="success" size="sm"><CheckCircle size={10} className="mr-1" />Passed</Pill>}
          <button onClick={() => setExpanded((e) => !e)} title={expanded ? 'Collapse' : 'Expand'}
                  className="p-1.5 rounded-v2-sm text-v2-ink-muted hover:bg-v2-muted hover:text-v2-ink">
            {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          <button onClick={onClose} title="Close"
                  className="p-1.5 rounded-v2-sm text-v2-ink-muted hover:bg-v2-muted hover:text-v2-ink">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-v2-page">
        {messages.length === 0 && !sending && (
          <div className="h-full flex flex-col items-center justify-center text-center px-6">
            <div className="w-14 h-14 rounded-v2-md bg-v2-cyan-soft text-v2-cyan flex items-center justify-center mb-4">
              <MessageSquare size={22} />
            </div>
            <div className="text-[14px] font-bold text-v2-ink">Chat with {agent.name}</div>
            <div className="text-[11.5px] text-v2-ink-muted mt-1 max-w-[280px] leading-relaxed">
              Type a message below to interact with the agent exactly as end users would. Policy enforcement, guardrails, and tool use are all live.
            </div>
            <div className="flex flex-wrap gap-1.5 mt-4 justify-center">
              {SUGGESTED_PROMPTS.map((q, i) => (
                <button key={i} type="button"
                        onClick={() => { setInput(q); setTimeout(() => inputRef.current?.focus(), 50); }}
                        className="text-[11px] px-2.5 py-1 rounded-v2-pill border border-v2-border-subtle bg-v2-surface text-v2-ink-secondary hover:border-v2-cyan hover:text-v2-cyan">
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => {
          if (msg.role === 'user') {
            return (
              <div key={i} className="flex justify-end">
                <div className="max-w-[80%] px-3 py-2 rounded-v2-md bg-v2-cyan text-white text-[12.5px] leading-relaxed shadow-v2-sm whitespace-pre-wrap break-words">
                  {msg.content}
                  <div className="text-[9px] text-white/60 mt-1 text-right">
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            );
          }
          if (msg.role === 'system') {
            return (
              <div key={i} className="flex justify-center">
                <div className={
                  'max-w-[90%] px-3 py-2 rounded-v2-sm text-[11px] text-center leading-relaxed border ' +
                  (msg.isError
                    ? 'bg-v2-danger-soft border-v2-danger-border text-v2-danger'
                    : 'bg-v2-muted border-v2-border-subtle text-v2-ink-muted')
                }>
                  {msg.isError && <XCircle size={11} className="inline align-middle mr-1" />}
                  {msg.content}
                </div>
              </div>
            );
          }
          /* assistant */
          const policyMeta = msg.policyAction ? POLICY_BADGE[msg.policyAction] : null;
          return (
            <div key={i} className="flex justify-start gap-2">
              <div className="w-7 h-7 rounded-v2-md bg-v2-cyan-soft text-v2-cyan flex items-center justify-center flex-shrink-0 mt-0.5">
                <Bot size={14} />
              </div>
              <div className="max-w-[82%] flex flex-col">
                {policyMeta && (
                  <div className="mb-1">
                    <Pill tone={policyMeta.tone} size="sm">
                      <policyMeta.Icon size={10} className="mr-1" />{policyMeta.label}
                    </Pill>
                  </div>
                )}
                {msg.toolResults && msg.toolResults.length > 0 && (
                  <div className="mb-1.5 px-2.5 py-1.5 rounded-v2-sm bg-v2-muted border border-v2-border-subtle text-[10px] text-v2-ink-secondary font-mono-v2">
                    <Zap size={10} className="inline align-middle mr-1 text-v2-warning" />
                    {msg.toolResults.length} tool{msg.toolResults.length !== 1 ? 's' : ''} executed
                    {msg.toolResults.map((tr, ti) => (
                      <div key={ti} className="mt-0.5 pl-3 border-l-2 border-v2-border-subtle">
                        <span className="text-v2-ink font-semibold">{tr.name || tr.tool || `Tool ${ti + 1}`}</span>
                        {tr.status && <span className={'ml-1.5 ' + (tr.status === 'success' ? 'text-v2-success' : 'text-v2-danger')}>({tr.status})</span>}
                      </div>
                    ))}
                  </div>
                )}
                <div className="px-3 py-2 rounded-v2-md bg-v2-surface border border-v2-border-subtle text-[12.5px] leading-relaxed text-v2-ink whitespace-pre-wrap break-words shadow-v2-sm">
                  {msg.content}
                </div>
                <div className="text-[9px] text-v2-ink-muted mt-0.5 pl-1">
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          );
        })}

        {sending && (
          <div className="flex justify-start gap-2">
            <div className="w-7 h-7 rounded-v2-md bg-v2-cyan-soft text-v2-cyan flex items-center justify-center flex-shrink-0 mt-0.5">
              <Bot size={14} />
            </div>
            <div className="px-3 py-2 rounded-v2-md bg-v2-surface border border-v2-border-subtle text-[12px] text-v2-ink-muted">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-v2-cyan animate-pulse" />
                <span className="w-1.5 h-1.5 rounded-full bg-v2-cyan animate-pulse" style={{ animationDelay: '0.15s' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-v2-cyan animate-pulse" style={{ animationDelay: '0.3s' }} />
              </span>
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-v2-border-subtle px-3 py-3 bg-v2-surface">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message and press Enter…"
            rows={2}
            disabled={sending}
            className="flex-1 text-[12.5px] px-2.5 py-1.5 rounded-v2-sm bg-v2-surface border border-v2-border-subtle resize-none focus:outline-none focus:border-v2-cyan focus:ring-1 focus:ring-v2-cyan v2-focus-ring"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!input.trim() || sending}
            className={
              'p-2 rounded-v2-sm flex-shrink-0 v2-focus-ring transition-colors duration-v2 ' +
              (!input.trim() || sending
                ? 'bg-v2-muted text-v2-ink-muted cursor-not-allowed'
                : 'bg-v2-cyan text-white hover:bg-v2-cyan-strong')
            }
            aria-label="Send message"
            title="Send (Enter)"
          >
            <Send size={14} />
          </button>
        </div>
        <div className="text-[9.5px] text-v2-ink-muted mt-1.5">
          Enter to send · Shift+Enter for newline · Tool use, guardrails, and policies are all live
        </div>
      </div>
    </div>
  );
}

export default TestRunSidePanelV2;
